'use client'

import { useMemo } from 'react'
import { contentData, themeData } from '@/lib/data'
import { getThemeForSection, formatFontFamily } from '@/lib/themeHelpers'
import NavbarComponent from './NavbarComponent'
import HeroSection from './HeroSection'
import AudienceSection from './AudienceSection'
import TestimonialsSection from './TestimonialsSection'
import InventorySection from './InventorySection'
import CampaignSection from './CampaignSection'
import ContactFAQSection from './ContactFAQSection'
import AboutUsSection from './AboutUsSection'
import FooterSection from './FooterSection'

export default function StorefrontPage() {
  const {
    gradStart, gradEnd, angle, fontFamily, radius, 
    imageUrl, logoUrl,
    sectionSettings, darkPrimary, lightPrimary
  } = themeData;
  
  // Generate themes for each section
  const heroTheme = useMemo(() => getThemeForSection('hero', sectionSettings, darkPrimary, lightPrimary), [sectionSettings, darkPrimary, lightPrimary]);
  const campaignTheme = useMemo(() => getThemeForSection('campaign', sectionSettings, darkPrimary, lightPrimary), [sectionSettings, darkPrimary, lightPrimary]);
  const audienceTheme = useMemo(() => getThemeForSection('audience', sectionSettings, darkPrimary, lightPrimary), [sectionSettings, darkPrimary, lightPrimary]);
  const testimonialsTheme = useMemo(() => getThemeForSection('testimonials', sectionSettings, darkPrimary, lightPrimary), [sectionSettings, darkPrimary, lightPrimary]);
  const inventoryTheme = useMemo(() => getThemeForSection('inventory', sectionSettings, darkPrimary, lightPrimary), [sectionSettings, darkPrimary, lightPrimary]);
  const contactfaqTheme = useMemo(() => getThemeForSection('contactfaq', sectionSettings, darkPrimary, lightPrimary), [sectionSettings, darkPrimary, lightPrimary]);
  const aboutUsTheme = useMemo(() => getThemeForSection('aboutus', sectionSettings, darkPrimary, lightPrimary), [sectionSettings, darkPrimary, lightPrimary]);
  const footerTheme = useMemo(() => getThemeForSection('footer', sectionSettings, darkPrimary, lightPrimary), [sectionSettings, darkPrimary, lightPrimary]);

  // Available sections for navbar navigation
  const availableSections = ['audience', 'inventory', 'contactfaq', 'aboutus'];

  return (
    <div className="min-h-screen w-full">
      {/* Fixed Navbar - positioned above all sections */}
      <NavbarComponent 
        theme={heroTheme}
        logoUrl={logoUrl}
        radius={radius}
        fontFamily={formatFontFamily(fontFamily)}
        availableSections={availableSections}
      />

      {/* Page Content with top padding to account for fixed navbar */}
      <div className="pt-20">
        {/* Hero Section */}
        <HeroSection
          theme={heroTheme}
          radius={radius}
          fontFamily={formatFontFamily(fontFamily)}
          imageUrl={imageUrl}
          content={contentData.hero}
        />

        {/* Audience Section */}
        <AudienceSection 
          surface={audienceTheme.surface}
          theme={audienceTheme}
          radius={radius}
          fontFamily={fontFamily}
          gradStart={gradStart}
          gradEnd={gradEnd}
          angle={angle}
          content={contentData.audience}
        />

        {/* Testimonials Section */}
        <TestimonialsSection 
          surface={testimonialsTheme.surface}
          theme={testimonialsTheme}
          radius={radius}
          fontFamily={fontFamily}
          gradStart={gradStart}
          gradEnd={gradEnd}
          angle={angle}
          content={contentData.testimonials}
        />

        {/* Inventory Section */}
        <InventorySection 
          surface={inventoryTheme.surface}
          theme={inventoryTheme}
          radius={radius}
          fontFamily={fontFamily}
          gradStart={gradStart}
          gradEnd={gradEnd}
          angle={angle}
          content={contentData.inventory}
        />

        {/* Campaign Section */}
        <CampaignSection 
          surface={campaignTheme.surface}
          theme={campaignTheme}
          radius={radius}
          fontFamily={fontFamily}
          iconWeight="regular"
          gradStart={gradStart}
          gradEnd={gradEnd}
          angle={angle}
          content={contentData.campaign}
        />

        {/* Contact/FAQ Section */}
        <ContactFAQSection 
          surface={contactfaqTheme.surface}
          theme={contactfaqTheme}
          radius={radius}
          fontFamily={fontFamily}
          iconWeight="regular"
          gradStart={gradStart}
          gradEnd={gradEnd}
          angle={angle}
          content={contentData.contactFAQ}
        />

        {/* About Us Section */}
        <AboutUsSection 
          surface={aboutUsTheme.surface}
          theme={aboutUsTheme}
          radius={radius}
          fontFamily={fontFamily}
          gradStart={gradStart}
          gradEnd={gradEnd}
          angle={angle}
          title={contentData.aboutUs.title}
          description={contentData.aboutUs.description}
          imageUrl={contentData.aboutUs.imageUrl}
        />

        {/* Footer Section */}
        <FooterSection 
          surface={footerTheme.surface}
          theme={footerTheme}
          radius={radius}
          fontFamily={fontFamily}
          iconWeight="regular"
          gradStart={gradStart}
          gradEnd={gradEnd}
          angle={angle}
          logoUrl={logoUrl}
          contactInfo={contentData.footer.contactInfo}
          socialLinks={contentData.footer.socialLinks}
          companyName={contentData.footer.companyName}
          description={contentData.footer.description}
        />
      </div>
    </div>
  );
}
